import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { useCachedState, useRefreshVersion } from './store';
import {
  Badge, Drawer, Empty, ErrorState, Field, Loading, OwnerSelect, Select, StateBadge, ago, date, dateInputToIso,
  priorityTone, severityTone, titleCase, useAssignees, useCan, useToast,
} from './ui';

const STATUSES = ['open', 'triaged', 'investigating', 'mitigating', 'monitoring', 'resolved', 'closed'];
const SEVERITIES = ['sev1', 'sev2', 'sev3', 'sev4'];
const TYPES = ['render_failure', 'render_stuck', 'transcription_failure', 'transcription_stuck', 'provider_outage', 'cost_anomaly', 'customer_report', 'security', 'other'];
const EVENT_ICONS = { detected: '⚡', created: '＋', note: '✎', task_added: '☐', task_updated: '☑', updated: '↻' };

export function Issues({ project, onClearProject, onOpenVideos }) {
  const can = useCan();
  // Arriving from a project's issue link means "show me everything on this
  // video", resolved included — the link may well have said "Closed".
  const [filters, setFilters] = useState({ search: '', status: '', severity: '', includeClosed: Boolean(project) });
  // The search box waits for a pause in typing; selects apply at once.
  const [search, setSearch] = useState('');
  useEffect(() => { const t = setTimeout(() => setFilters((f) => (f.search === search ? f : { ...f, search })), 300); return () => clearTimeout(t); }, [search]);
  const [data, setData] = useCachedState('/incidents?');
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [toast, toastNode] = useToast();
  const refreshVersion = useRefreshVersion();

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const params = new URLSearchParams();
    if (filters.search.trim()) params.set('search', filters.search.trim());
    if (filters.status) params.set('status', filters.status);
    if (filters.severity) params.set('severity', filters.severity);
    if (filters.includeClosed) params.set('includeClosed', 'true');
    if (project) params.set('projectId', project.id);
    try { setData(await api(`/incidents?${params}`)); } catch (caught) { setError(caught.message); } finally { setLoading(false); }
  }, [filters.status, filters.severity, filters.includeClosed, project, filters.search, refreshVersion]);
  useEffect(() => { void load(); }, [load]);

  const set = (key) => (event) => setFilters((f) => ({ ...f, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));
  const items = data?.items ?? [];
  const counts = items.reduce((acc, i) => { acc[i.severity] = (acc[i.severity] ?? 0) + 1; return acc; }, {});

  return <>
    <section className="page-title"><div><div className="eyebrow">Issues</div><h1>Issues</h1><p>Failed and stuck pipelines, grouped automatically and assigned to a person.</p></div>
      {can('operate_incidents') && <button className="primary" onClick={() => setCreating(true)}>+ New incident</button>}</section>

    {project && <div className="filter-banner"><span>Showing incidents linked to <b>{project.title || 'Untitled project'}</b> <small>{project.id}</small></span><button className="text-button" onClick={onClearProject}>Show all incidents ✕</button></div>}

    <section className="toolbar">
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search key, title, or provider" />
      <Select value={filters.status} onChange={set('status')} label="Status" options={[['', 'Open statuses'], ...STATUSES.map((s) => [s, titleCase(s)])]} />
      <Select value={filters.severity} onChange={set('severity')} label="Severity" options={[['', 'All severities'], ...SEVERITIES.map((s) => [s, s.toUpperCase()])]} />
      <label className="check-inline"><input type="checkbox" checked={filters.includeClosed} onChange={set('includeClosed')} /> Include resolved</label>
    </section>

    <div className="sev-strip">{SEVERITIES.map((s) => <span key={s} className={`sev ${severityTone(s)}`}><b>{counts[s] ?? 0}</b> {s.toUpperCase()}</span>)}<span className="sev muted"><b>{data?.page?.total ?? 0}</b> total</span></div>

    {error ? <ErrorState message={error} retry={load} /> : loading && !data ? <Loading /> : items.length ? (
      <div className={`incident-list ${loading ? 'is-loading' : ''}`}>
        {items.map((issue) => <button key={issue.id} className="incident-card" onClick={() => setOpenId(issue.id)}>
          <div className="incident-card-head"><Badge tone={severityTone(issue.severity)}>{issue.severity.toUpperCase()}</Badge><StateBadge status={issue.status} /><span className="incident-key">{issue.key}</span><span className="incident-age">{ago(issue.detected_at)}</span></div>
          <h3>{issue.title}</h3>
          {issue.description && <p>{issue.description}</p>}
          <div className="incident-card-meta">
            <span><i>Type</i>{titleCase(issue.type)}</span>
            <span><i>Provider</i>{issue.affected_provider || '—'}</span>
            <span><i>Impact</i>{issue.affected_count} linked</span>
            <span><i>Tasks</i>{issue.open_task_count} open / {issue.task_count}</span>
            <span><i>Owner</i>{issue.owner_name || issue.owner_email || <em className="unassigned">Unassigned</em>}</span>
          </div>
        </button>)}
      </div>
    ) : <article className="panel"><Empty title={project ? 'No incidents linked to this project' : filters.includeClosed || filters.status ? 'Nothing matches these filters' : 'All clear for now'} body={project ? 'The open-issue count comes from incident links; the incident may have been resolved since.' : 'Failed and stuck pipelines surface here automatically every five minutes.'} /></article>}

    {openId && <IncidentDrawer id={openId} onClose={() => { setOpenId(null); void load(); }} onOpenVideos={onOpenVideos} toast={toast} />}
    {creating && <NewIncident onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); void load(); setOpenId(id); toast('Incident opened'); }} />}
    {toastNode}
  </>;
}

function IncidentDrawer({ id, onClose, onOpenVideos, toast }) {
  const can = useCan();
  const assignees = useAssignees();
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [closing, setClosing] = useState(null); // 'resolved' | 'closed' | null
  const [resolution, setResolution] = useState('');
  const [rootCause, setRootCause] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskPriority, setTaskPriority] = useState('medium');
  const [taskOwner, setTaskOwner] = useState(null);
  const [taskDue, setTaskDue] = useState('');

  const refresh = useCallback(async () => {
    try { const next = await api(`/incidents/${id}`); setDetail(next); setResolution(next.incident.resolution ?? ''); setRootCause(next.incident.root_cause ?? ''); }
    catch (caught) { setError(caught.message); }
  }, [id]);
  useEffect(() => { void refresh(); }, [refresh]);

  const patch = async (body, message = 'Saved') => {
    setBusy(true);
    try { await api(`/incidents/${id}`, { method: 'PATCH', body: JSON.stringify(body) }); await refresh(); toast(message); return true; }
    catch (caught) { toast(caught.message, 'danger'); return false; }
    finally { setBusy(false); }
  };

  if (error) return <Drawer title="Incident" onClose={onClose}><ErrorState message={error} retry={refresh} /></Drawer>;
  if (!detail) return <Drawer title="Incident" onClose={onClose}><Loading label="Loading incident…" /></Drawer>;
  const { incident: i, events, links, tasks } = detail;
  const isClosed = i.status === 'resolved' || i.status === 'closed';
  const editable = can('operate_incidents') && !isClosed;
  const openTasks = tasks.filter((t) => !['done', 'cancelled'].includes(t.status));

  const addTask = async (event) => {
    event.preventDefault();
    if (taskTitle.trim().length < 3) return;
    setBusy(true);
    try {
      await api('/tasks', { method: 'POST', body: JSON.stringify({ title: taskTitle.trim(), priority: taskPriority, incidentId: id, ownerUserId: taskOwner ?? undefined, dueAt: dateInputToIso(taskDue) ?? undefined }) });
      setTaskTitle(''); setTaskDue(''); await refresh(); toast('Task added');
    } catch (caught) { toast(caught.message, 'danger'); } finally { setBusy(false); }
  };
  const setTaskStatus = async (task, status) => {
    setBusy(true);
    try { await api(`/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify({ status }) }); await refresh(); }
    catch (caught) { toast(caught.message, 'danger'); } finally { setBusy(false); }
  };
  const addNote = async (event) => {
    event.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try { await api(`/incidents/${id}/events`, { method: 'POST', body: JSON.stringify({ body: note.trim() }) }); setNote(''); await refresh(); }
    catch (caught) { toast(caught.message, 'danger'); } finally { setBusy(false); }
  };
  const confirmClose = async () => {
    if (!resolution.trim()) { toast('Write a resolution first.', 'danger'); return; }
    if (openTasks.length && !window.confirm(`${openTasks.length} task${openTasks.length > 1 ? 's are' : ' is'} still open. Resolve the incident anyway?`)) return;
    if (await patch({ status: closing, resolution: resolution.trim(), rootCause: rootCause.trim() || null }, `Incident ${closing}`)) setClosing(null);
  };
  const saveEdit = async () => { if (await patch(draft, 'Incident updated')) setEditing(false); };

  const footer = can('operate_incidents') ? (
    isClosed
      ? <><span className="foot-note">Resolved {date(i.resolved_at)}</span><button className="subtle" disabled={busy} onClick={() => patch({ status: 'open' }, 'Incident reopened')}>Reopen</button></>
      : closing
        ? <><button className="subtle" disabled={busy} onClick={() => setClosing(null)}>Cancel</button><button className="primary" disabled={busy} onClick={confirmClose}>Confirm {closing}</button></>
        : <><button className="subtle" disabled={busy} onClick={() => setClosing('closed')}>Close without fix</button><button className="primary good" disabled={busy} onClick={() => setClosing('resolved')}>✓ Resolve incident</button></>
  ) : null;

  return <Drawer wide title={i.title} subtitle={`${i.key} · ${titleCase(i.source)} · detected ${ago(i.detected_at)}`} onClose={onClose} footer={footer}>
    <div className="incident-status-row">
      <Badge tone={severityTone(i.severity)}>{i.severity.toUpperCase()}</Badge>
      <StateBadge status={i.status} />
      {editable && <Select size="sm" label="Status" value={i.status} disabled={busy} onChange={(e) => { const s = e.target.value; if (s === 'resolved' || s === 'closed') setClosing(s); else void patch({ status: s }, `Status → ${titleCase(s)}`); }} options={STATUSES.map((s) => [s, titleCase(s)])} />}
      {editable && <Select size="sm" label="Severity" value={i.severity} disabled={busy} onChange={(e) => patch({ severity: e.target.value }, `Severity → ${e.target.value.toUpperCase()}`)} options={SEVERITIES.map((s) => [s, s.toUpperCase()])} />}
      {editable && <span className="grow" />}
      {editable && !editing && <button className="text-button" onClick={() => { setDraft({ title: i.title, description: i.description ?? '', type: i.type, affectedProvider: i.affected_provider ?? '' }); setEditing(true); }}>Edit details</button>}
    </div>

    {closing && <div className="closure-box">
      <b>{closing === 'resolved' ? 'Resolve this incident' : 'Close without a fix'}</b>
      <p>{closing === 'resolved' ? 'Write down what fixed it. This outlives whoever remembers the outage.' : 'Closing marks the incident finished without a fix — say why (duplicate, not reproducible, accepted risk).'}</p>
      <Field label="Root cause"><textarea rows={2} value={rootCause} onChange={(e) => setRootCause(e.target.value)} placeholder="What actually went wrong?" /></Field>
      <Field label={closing === 'resolved' ? 'Resolution (required)' : 'Reason (required)'}><textarea rows={3} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder={closing === 'resolved' ? 'What was changed, retried, or rolled back?' : 'Why is this being closed?'} /></Field>
      {openTasks.length > 0 && <small className="warn-text">⚠ {openTasks.length} linked task{openTasks.length > 1 ? 's are' : ' is'} still open.</small>}
    </div>}

    {editing ? <div className="edit-box">
      <Field label="Title"><input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} /></Field>
      <Field label="Description"><textarea rows={4} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} /></Field>
      <div className="field-row">
        <Field label="Type"><Select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} options={TYPES.map((t) => [t, titleCase(t)])} /></Field>
        <Field label="Affected provider"><input value={draft.affectedProvider} onChange={(e) => setDraft({ ...draft, affectedProvider: e.target.value })} placeholder="groq, remotion_lambda…" /></Field>
      </div>
      <div className="btn-row"><button className="subtle" onClick={() => setEditing(false)}>Cancel</button><button className="primary" disabled={busy} onClick={saveEdit}>Save</button></div>
    </div> : <>
      {i.description && <p className="incident-desc">{i.description}</p>}
      <dl className="detail-grid">
        <div><dt>Type</dt><dd>{titleCase(i.type)}</dd></div>
        <div><dt>Provider</dt><dd>{i.affected_provider || '—'}</dd></div>
        <div><dt>Owner</dt><dd>{editable ? <OwnerSelect value={i.owner_user_id} assignees={assignees} onChange={(v) => patch({ ownerUserId: v }, v ? 'Owner assigned' : 'Owner cleared')} /> : (i.owner_name || i.owner_email || 'Unassigned')}</dd></div>
        <div><dt>Started</dt><dd>{date(i.started_at)}</dd></div>
        <div><dt>Detected</dt><dd>{date(i.detected_at)}</dd></div>
        <div><dt>Acknowledged</dt><dd>{date(i.acknowledged_at)}</dd></div>
        <div><dt>Mitigated</dt><dd>{date(i.mitigated_at)}</dd></div>
        <div><dt>Resolved</dt><dd>{date(i.resolved_at)}</dd></div>
        {i.fingerprint && <div className="span-2"><dt>Fingerprint</dt><dd><code>{i.fingerprint}</code></dd></div>}
        {i.root_cause && <div className="span-2"><dt>Root cause</dt><dd>{i.root_cause}</dd></div>}
        {i.resolution && <div className="span-2"><dt>Resolution</dt><dd>{i.resolution}</dd></div>}
      </dl>
    </>}

    <section className="drawer-section">
      <h3>Tasks <small>{openTasks.length} open · {tasks.length} total</small></h3>
      {tasks.length ? <ul className="task-mini-list">{tasks.map((t) => <li key={t.id} className={['done', 'cancelled'].includes(t.status) ? 'is-done' : ''}>
        {can('operate_tasks') ? <input type="checkbox" checked={t.status === 'done'} disabled={busy} onChange={(e) => setTaskStatus(t, e.target.checked ? 'done' : 'todo')} /> : <span className={`check ${t.status === 'done' ? 'on' : ''}`} />}
        <div><b>{t.title}</b><small>{t.owner_name || t.owner_email || 'Unassigned'}{t.due_at ? ` · due ${date(t.due_at)}` : ''}</small></div>
        <Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>
        {can('operate_tasks') && <Select size="sm" label="Task status" value={t.status} disabled={busy} onChange={(e) => setTaskStatus(t, e.target.value)} options={['todo', 'in_progress', 'blocked', 'done', 'cancelled'].map((s) => [s, titleCase(s)])} />}
      </li>)}</ul> : <p className="muted-text">No tasks yet. Break the fix down into steps so it has an owner.</p>}
      {can('operate_tasks') && !isClosed && <form className="task-add" onSubmit={addTask}>
        <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} placeholder="Add a task for this incident…" />
        <Select value={taskPriority} onChange={(e) => setTaskPriority(e.target.value)} label="Priority" options={['urgent', 'high', 'medium', 'low'].map((p) => [p, titleCase(p)])} />
        <OwnerSelect value={taskOwner} onChange={setTaskOwner} assignees={assignees} />
        <input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} />
        <button className="primary" disabled={busy || taskTitle.trim().length < 3}>Add</button>
      </form>}
    </section>

    <section className="drawer-section">
      <h3>Linked entities <small>{links.length}</small></h3>
      {links.length ? <ul className="link-list">{links.map((l) => <li key={`${l.entity_type}-${l.entity_id}`}>
        <Badge>{titleCase(l.entity_type)}</Badge>
        <div><b>{l.label || <em>No longer exists</em>}</b><small>{l.detail || l.entity_id}</small></div>
        {l.project_id && onOpenVideos && <button className="text-button" onClick={() => onOpenVideos(l.project_id)}>Open video →</button>}
      </li>)}</ul> : <p className="muted-text">Nothing linked.</p>}
    </section>

    <section className="drawer-section">
      <h3>Timeline <small>{events.length}</small></h3>
      <ol className="timeline">{events.map((e) => <li key={e.id}><i>{EVENT_ICONS[e.event_type] ?? (e.event_type.startsWith('status_') ? '→' : '•')}</i><div><b>{e.body || titleCase(e.event_type)}</b><small>{e.actor_name || e.actor_email || 'System'} · {date(e.created_at)}</small></div></li>)}</ol>
      {can('operate_incidents') && <form className="note-add" onSubmit={addNote}><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note to the timeline…" /><button className="subtle" disabled={busy || !note.trim()}>Add note</button></form>}
    </section>
  </Drawer>;
}

function NewIncident({ onClose, onCreated }) {
  const assignees = useAssignees();
  const [form, setForm] = useState({ title: '', description: '', type: 'other', severity: 'sev3', affectedProvider: '', ownerUserId: null });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const submit = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const body = { ...form, description: form.description || undefined, affectedProvider: form.affectedProvider || undefined, ownerUserId: form.ownerUserId || undefined };
      const created = await api('/incidents', { method: 'POST', body: JSON.stringify(body) });
      onCreated(created.id);
    } catch (caught) { setError(caught.message); } finally { setBusy(false); }
  };
  return <Drawer title="New incident" subtitle="MANUAL REPORT" onClose={onClose} footer={<><button className="subtle" onClick={onClose}>Cancel</button><button className="primary" form="new-incident" disabled={busy || form.title.trim().length < 3}>{busy ? 'Opening…' : 'Open incident'}</button></>}>
    <form id="new-incident" onSubmit={submit} className="form-stack">
      <Field label="Title"><input autoFocus value={form.title} onChange={set('title')} placeholder="Short, specific: 'Groq returning 429 for all chunks'" /></Field>
      <Field label="What's happening?"><textarea rows={4} value={form.description} onChange={set('description')} placeholder="Symptoms, who is affected, when it started." /></Field>
      <div className="field-row">
        <Field label="Severity"><Select value={form.severity} onChange={set('severity')} options={SEVERITIES.map((s) => [s, s.toUpperCase()])} /></Field>
        <Field label="Type"><Select value={form.type} onChange={set('type')} options={TYPES.map((t) => [t, titleCase(t)])} /></Field>
      </div>
      <div className="field-row">
        <Field label="Affected provider"><input value={form.affectedProvider} onChange={set('affectedProvider')} placeholder="groq, openai, remotion_lambda…" /></Field>
        <Field label="Owner"><OwnerSelect value={form.ownerUserId} onChange={(v) => setForm({ ...form, ownerUserId: v })} assignees={assignees} /></Field>
      </div>
      {error && <div className="form-error">{error}</div>}
    </form>
  </Drawer>;
}
